import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, SxProps, Theme, useTheme } from "@mui/material";

interface AudioWaveformProps {
  levels: number[];
  highlightedBars?: number;
  isPaused?: boolean;
  barWidth?: number;
  gap?: number | string;
  height?: number;
  activeColor?: string;
  inactiveColor?: string;
  sx?: SxProps<Theme>;
  isMobile?: boolean;
  onSeek?: (fraction: number) => void;
}

const AudioWaveform: React.FC<AudioWaveformProps> = ({
  levels,
  highlightedBars = 0,
  isPaused = false,
  barWidth = 3,
  gap = "2px",
  height = 36,
  activeColor,
  inactiveColor,
  sx,
  isMobile = false,
  onSeek,
}) => {
  const theme = useTheme();
  const resolvedActiveColor = activeColor ?? theme.nawy.colors.semantic.border.accent;
  const resolvedInactiveColor = inactiveColor ?? theme.nawy.colors.semantic.border.primary;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [dragFraction, setDragFraction] = useState<number | null>(null);
  const isDragging = dragFraction !== null;

  const fractionFromClientX = useCallback((clientX: number): number | null => {
    const el = containerRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return null;
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  }, []);

  const handlePointerDown = onSeek
    ? (e: React.PointerEvent<HTMLDivElement>) => {
        if (e.button !== undefined && e.button !== 0) return;
        const fraction = fractionFromClientX(e.clientX);
        if (fraction === null) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        setDragFraction(fraction);
      }
    : undefined;

  const handlePointerMove = onSeek
    ? (e: React.PointerEvent<HTMLDivElement>) => {
        if (!isDragging) return;
        const fraction = fractionFromClientX(e.clientX);
        if (fraction === null) return;
        setDragFraction(fraction);
      }
    : undefined;

  const endDrag = useCallback(
    (commit: boolean) => {
      setDragFraction((prev) => {
        if (prev !== null && commit && onSeek) onSeek(prev);
        return null;
      });
    },
    [onSeek],
  );

  const handlePointerUp = onSeek
    ? (e: React.PointerEvent<HTMLDivElement>) => {
        if (!isDragging) return;
        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
          e.currentTarget.releasePointerCapture(e.pointerId);
        }
        endDrag(true);
      }
    : undefined;

  const handlePointerCancel = onSeek
    ? () => {
        if (isDragging) endDrag(false);
      }
    : undefined;

  // Safety: cancel drag if component unmounts or onSeek removed mid-drag
  useEffect(() => {
    if (!onSeek && isDragging) setDragFraction(null);
  }, [onSeek, isDragging]);

  const barCount = levels.length;
  const effectiveHighlighted = isDragging
    ? Math.round((dragFraction as number) * barCount)
    : highlightedBars;

  return (
    <Box
      ref={containerRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      sx={{
        flex: 1,
        minWidth: 0,
        height,
        display: "flex",
        alignItems: "center",
        gap,
        overflow: "hidden",
        cursor: onSeek ? "pointer" : "default",
        touchAction: onSeek ? "none" : undefined,
        userSelect: "none",
        ...sx,
      }}
    >
      {levels.map((level, i) => {
        const isHighlighted = i < effectiveHighlighted;
        return (
          <Box
            key={i}
            sx={{
              ...(isMobile ? { flex: 1, minWidth: 0, maxWidth: barWidth } : { width: barWidth }),
              height: isPaused ? 4 : level,
              backgroundColor: isHighlighted ? resolvedActiveColor : resolvedInactiveColor,
              borderRadius: 1,
              transition: isDragging
                ? "none"
                : isPaused
                  ? "height 0.1s ease-out"
                  : "height 0.05s ease-out",
              pointerEvents: "none",
            }}
          />
        );
      })}
    </Box>
  );
};

export default AudioWaveform;
